import { inject, injectable } from "@launchtray/tsyringe-async";
import { SceneManager } from "../../../../scene/SceneManager";
import { ModuleFlowExecutor } from "../../../composition/module/ModuleFlowExecutor";
import { TopPanel } from "../../game/components/toppanel/TopPanel";
import { ShopModule } from "./ShopModule";
import { ShopPopupScene } from "./scene/ShopPopupScene";
import { ILogger } from "../../../logger/ILogger";
import { CancellationTokenSource } from "async/CancellationTokenSource";
import { CancellationToken } from "async/CancellationToken";
import { IConnectionModel } from "by/gamefactory/cityversetycoon/networking/IConnectionModel";
import { nameof } from "bindable-data";
import { IShopModel } from "./model/IShopModel";
import { AsyncOperationNotification } from "../../common/components/AsyncOperationNotification";
import { Web3Model } from "../../../../web3/models/Web3Model";
import { IInventory } from "../../../../web3/models/IInventory";

type ShopPopupSceneFactory = (cancellationToken: CancellationToken) => Promise<ShopPopupScene>;

@injectable()
export class ShopModuleFlowExecutor extends ModuleFlowExecutor<ShopModule, void> {
    private readonly sceneManager: SceneManager;
    private readonly shopModel: IShopModel;
    private readonly connectionModel: IConnectionModel;
    private readonly web3Model: Web3Model;
    private readonly inventory: IInventory;
    private readonly topPanel: TopPanel;
    private readonly asyncOperationNotification: AsyncOperationNotification;
    private readonly logger: ILogger;

    private readonly cancellationTokenSource: CancellationTokenSource;

    private topPanelOnNotificationLayerAfterExecute: boolean;
    private topPanelChildrenInteractive: boolean;

    public constructor(
        sceneManager: SceneManager,
        @inject("IShopModel") shopModel: IShopModel,
        @inject("IConnectionModel") connectionModel: IConnectionModel,
        web3Model: Web3Model,
        @inject("IInventory") inventory: IInventory,
        topPanel: TopPanel,
        asyncOperationNotification: AsyncOperationNotification,
        @inject("ILogger") logger: ILogger
    ) {
        super();
        this.sceneManager = sceneManager;
        this.shopModel = shopModel;
        this.connectionModel = connectionModel;
        this.web3Model = web3Model;
        this.inventory = inventory;
        this.topPanel = topPanel;
        this.asyncOperationNotification = asyncOperationNotification;
        this.logger = logger;

        this.cancellationTokenSource = new CancellationTokenSource();
        this.connectionModel.propertyChanged.add(this.onConnectionModelPropertyChanged, this);
        this.topPanel.onLayerChanged.add(this.onTopPanelLayerChanged, this);
    }

    public async execute(module: ShopModule): Promise<void> {
        const token = this.cancellationTokenSource.token;
        try {
            this.asyncOperationNotification.show();
            await this.web3Model.waitSmartWalletConnection();
            await Promise.all([this.inventory.update(), this.shopModel.getShopItems()]);

            if (token.isCancellationRequested) {
                return;
            }

            this.topPanelOnNotificationLayerAfterExecute = this.topPanel.onNotificationLayer;
            this.topPanelChildrenInteractive = this.topPanel.interactiveChildren;
            if (!this.topPanelOnNotificationLayerAfterExecute) {
                this.topPanel.bringToFront(true);
            }
            this.topPanel.interactiveChildren = false;

            const sceneFactory: ShopPopupSceneFactory = await module.resolve("ShopPopupSceneFactory");
            const scene = await sceneFactory(token);

            if (token.isCancellationRequested) {
                scene.view.destroy();
                return;
            }

            this.asyncOperationNotification.hide();
            await this.sceneManager.showViewableAsync(scene);
        }
        catch (e: unknown) {
            this.logger.error("Exception on executing ShopModule flow: ", e);
        }
        finally {
            this.asyncOperationNotification.hide();
            this.cancellationTokenSource.dispose();
            this.connectionModel.propertyChanged.remove(this.onConnectionModelPropertyChanged, this);
            this.topPanel.onLayerChanged.remove(this.onTopPanelLayerChanged, this);
            this.topPanel.bringToOriginParent();
            if (this.topPanelChildrenInteractive) {
                this.topPanel.interactiveChildren = true;
            }
        }
    }

    private onConnectionModelPropertyChanged(model: IConnectionModel, key: string): void {
        switch (key) {
            case nameof(model, "connected"):
                if (!model.connected.value) {
                    this.logger.debug("MyBuildingsModule cancellation requested, reason: disconnect");
                    this.cancellationTokenSource.cancel();
                }
                break;
        }
    }

    private onTopPanelLayerChanged(): void {
        this.topPanelOnNotificationLayerAfterExecute = this.topPanel.onNotificationLayer;
        if (!this.topPanel.onNotificationLayer) {
            this.topPanel.bringToFront(true);
        }
    }
}